/**
 * Percentage discounts, the same rule as the API's `src/utils/money.js`
 * (refactoring item 2, APP-191): the DISCOUNT is rounded half up to the cent
 * and then subtracted; the final price is never rounded on its own.
 * R$ 10.01 at 50% is a discount of R$ 5.01 and a price of R$ 5.00.
 *
 * Everything runs in integer cents, with the percent taken to two decimals,
 * so no float error can move a result by a cent. Every currency the API
 * accepts has two decimals.
 */

/** Whole cents of `amount` (a decimal currency value). */
const toCents = (amount: number) => Math.round(amount * 100)

/** Discount and final price, in cents, for `priceCents` less `percent`% (0-100). */
export const discountCents = (priceCents: number, percent: number) => {
  const hundredths = Math.round((Number.isFinite(percent) ? percent : 0) * 100)
  const discount = Math.floor((priceCents * hundredths + 5000) / 10000)
  return { discountCents: discount, finalCents: priceCents - discount }
}

/** The discount `percent`% takes off `amount`, rounded half up to the cent. */
export const discountOf = (amount: number, percent: number) => discountCents(toCents(amount), percent).discountCents / 100

/** `amount` less `percent`%: the amount minus the rounded discount. */
export const afterDiscount = (amount: number, percent: number) => discountCents(toCents(amount), percent).finalCents / 100

/* ------------------------------------------------------------------------ */
/* Formatting (refactoring item U4): the one way the app writes money.       */
/* ------------------------------------------------------------------------ */

/** The `currencyFormat` the API returns for a community (CommunityContext re-exports it). */
export interface CurrencyFormat {
  symbol: string
  symbolPosition: 'before' | 'after'
  decimalSeparator: string
  thousandsSeparator: string
  decimalDigits: number
  pattern: string
  example: string
}

/** What `formatMoney` needs from the community: its currency and how it writes it. */
export interface MoneyCommunity {
  currency?: string | null
  currencyFormat?: CurrencyFormat | null
}

export interface MoneyDigits {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

const EMPTY = '—'

/** `amount` in the community's own format: "R$ 1.234,50", "1.234,50 €", "-R$ 10,00". */
const communityPattern = (amount: number, format: CurrencyFormat): string => {
  const digits = format.decimalDigits
  const fixed = Math.abs(amount).toFixed(digits)
  /* "-0,00" is not a debt: the sign only stays when the rounded value is not zero. */
  const negative = amount < 0 && Number(fixed) !== 0
  const [int, dec] = fixed.split('.')
  const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator)
  const number = dec ? `${withThousands}${format.decimalSeparator}${dec}` : withThousands
  const body = format.symbolPosition === 'before' ? `${format.symbol} ${number}` : `${number} ${format.symbol}`
  return negative ? `-${body}` : body
}

/**
 * The one money formatter of the app (U4). Money in the community's currency
 * is written in the community's format (the `currencyFormat` the API derives
 * from its country), whatever language the reader uses: an English reader of
 * a Brazilian community sees "R$ 1.234,00" on Monetização and on Vendas alike.
 * Any other currency (Memberfy's own USD prices, an ad priced in dollars) is
 * written by `Intl` in the reader's language.
 *
 * There is no default currency: without `currency` it takes the community's,
 * and without either it writes a plain number rather than guess a symbol.
 * `digits` widens the fraction (provider costs in USD with four decimals) and
 * always goes through `Intl`.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  locale: string,
  community?: MoneyCommunity | null,
  digits?: MoneyDigits,
): string {
  if (amount === null || amount === undefined || amount === '') return EMPTY
  const value = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(value)) return EMPTY
  const code = (currency || community?.currency || '').toUpperCase()
  const format = community?.currencyFormat
  const isCommunityCurrency = Boolean(format) && (!code || code === (community?.currency || '').toUpperCase())
  if (format && isCommunityCurrency && !digits) return communityPattern(value, format)
  if (!code) return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, ...digits }).format(value)
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code, ...digits }).format(value)
  } catch {
    /* An unknown code: say which currency it is rather than throw on render. */
    return `${code} ${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`
  }
}
